import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input,
  OnChanges, OnDestroy, OnInit, Optional, Output, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {Subscription} from 'rxjs';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Translatable, TranslationEventService, TranslationService} from '@ngmedax/translation';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {ConfigService} from '@ngmedax/config';
import {PdfFormService} from '@ngmedax/pdf-form';
import {ValueService} from '@ngmedax/value';

import {VARIABLE_NAME_PATTERN} from './validator/variable-name-pattern';
import {GDT_VARIABLE_NAME_RULES, VARIABLE_NAME_RULES, variableNameMessage} from '../../../shared/variable-name-hint';
import {duplicateVariableNameValidator} from './validator/duplicate-variable-name.validator';
import {QuestionnaireVariablesService} from '../../../services/questionnaire-variables.service';
import {QuestionnaireStateService} from '../../../services/questionnaire-state.service';
import {DomHelperService} from '../../../services/dom-helper.service';
import {QuestionnaireEditor} from '../../../../types';
import {TRANSLATION_EDITOR_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';



// hack to inject decorator declarations. must occur before class declaration!
export interface VariablesComponent extends Translatable {}

@Component({
  selector: 'app-qa-variables',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './variables.component.html',
  styleUrls: ['./variables.component.css', '../../../shared/settings-controls.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class VariablesComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() container: Questionnaire.Container;
  @Input() focusUpload = false;
  @Output() uploadFocused = new EventEmitter<void>();
  @ViewChildren('variableNameInput', {read: ElementRef}) private nameInputs: QueryList<ElementRef<HTMLElement>>;
  private inputChanges: Subscription;
  private uploadFocusHandled = false;
  private destroyed = false;

  /** Handles repeated focus requests without changing stored upload mappings. */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.focusUpload) {
      this.uploadFocusHandled = false;
      this.scheduleUploadFocus();
    }
  }

  /** Waits for asynchronously initialized variable fields to appear. */
  public ngAfterViewInit(): void {
    this.inputChanges = this.nameInputs.changes.subscribe(() => this.scheduleUploadFocus());
    this.scheduleUploadFocus();
  }

  /** Cancels pending focus work when the user leaves this properties tab. */
  public ngOnDestroy(): void {
    this.destroyed = true;
    this.inputChanges && this.inputChanges.unsubscribe();
  }

  /** Focuses the upload name and explains its prefix semantics after rendering. */
  private scheduleUploadFocus(): void {
    Promise.resolve().then(() => {
      if (this.destroyed || !this.focusUpload || this.uploadFocusHandled || !this.nameInputs || !this.variablesForm) {
        return;
      }
      const index = this.nameInputs.toArray().findIndex(input => input.nativeElement.getAttribute('data-variable-scope') === 'upload');
      if (index < 0) {
        return;
      }
      this.uploadFocusHandled = true;
      const uploadControl = this.variablesForm.get('upload');
      uploadControl && uploadControl.reset(this.questionnaireVariables.getMappedVariableName('upload', this.container.pathHash));
      this.ref.detectChanges();
      const input = this.nameInputs.toArray()[index].nativeElement;
      input.scrollIntoView({block: 'center', inline: 'nearest'});
      input.focus({preventScroll: true});
      this.uploadHelp = true;
      this.ref.markForCheck();
      this.uploadFocused.emit();
    });
  }
  @Input() disabledScopes: {[scopeName: string]: boolean} = {};

  /**
   * Locale for questionnaire. Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi language support
   * @type {string}
   */
  public locale = 'de_DE';

  /**
   * Scope names for questionnaire variables
   *
   * @type {string[]}
   */
  public scopeNames = [];

  /**
   * Variable scopes configuration
   *
   * @type {QuestionnaireEditor.VariableScopes}
   */
  public variableScopes: QuestionnaireEditor.VariableScopes = null;

  /**
   * Form for variables
   *
   * @type {FormGroup}
   */
  public variablesForm: FormGroup;

  /**
   * Reference to current questionnaire
   *
   * @type {Questionnaire}
   */
  private questionnaire: Questionnaire;

  /**
   * Shows the upload prefix explanation below the upload field after a focus request
   */
  public uploadHelp = false;

  /**
   * Injects dependencies
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    @Optional() private translationEvents: TranslationEventService,
    @Optional() private pdfFormService: PdfFormService,
    private questionnaireVariables: QuestionnaireVariablesService,
    private questionnaireState: QuestionnaireStateService,
    private config: ConfigService,
    private value: ValueService,
    private formBuilder: FormBuilder,
    private domHelper: DomHelperService,
    private ref: ChangeDetectorRef
  ) {
    this.translationEvents && this.translationEvents.onLocaleChanged().subscribe(() => this.ref.markForCheck());
  }

  /**
   * Initializes conditions
   */
  public ngOnInit() {
    this.questionnaire = this.questionnaireState.getQuestionnaire();
    this.scopeNames = this.questionnaireVariables.getVariableScopeNames();
    this.variableScopes = this.questionnaireVariables.getVariableScopes();

    if (!this.scopeNames || !this.scopeNames.length) {
      return;
    }

    this.initForm().then(() => {
      if (!this.destroyed) {
        this.populateForm();
        this.scheduleUploadFocus();
      }
    });
  }

  /**
   * Saves variable mapping
   */
  public onSave(el: HTMLElement, formEl: AbstractControl, scope: string) {
    const pathHash = this.container.pathHash;
    const variableName = formEl.value;
    const ctrlName = this.getFormControlNameForReturnValue(scope);
    const returnValue = this.variablesForm.get(ctrlName) ? this.variablesForm.get(ctrlName).value : '';
    const options = this.variableScopes[scope].allowReturnValue && returnValue ?
      {returnValue: {[this.locale]: returnValue}} : {};

    // a dirty control shows its problem below the field
    formEl.markAsDirty();
    this.runValidationOnFormEl(formEl);

    if (formEl.errors) {
      return;
    }

    this.questionnaireVariables.addVariableMapping(scope, variableName, pathHash, options);
    this.questionnaireState.onUpdateElement.emit({element: null, position: null});
    formEl.reset(variableName);
    scope === 'upload' && (this.uploadHelp = false);
    this.onClean(el);
  }

  /**
   * Resets variable mapping
   */
  public onReset(el: HTMLElement, formEl: AbstractControl, scope: string) {
    const pathHash = this.container.pathHash;
    const ctrlName = this.getFormControlNameForReturnValue(scope);
    const previousVariableName = this.questionnaireVariables.getMappedVariableName(scope, pathHash);
    const options = this.questionnaireVariables.getMappedVariableOptions(scope, pathHash, previousVariableName);

    this.variablesForm.get(ctrlName) && this.variablesForm.get(ctrlName).setValue('');

    if (options && options.returnValue && options.returnValue[this.locale]) {
      const returnValue = options.returnValue[this.locale];
      this.variablesForm.get(ctrlName) && this.variablesForm.get(ctrlName).setValue(returnValue);
    }

    formEl.reset(previousVariableName);
    scope === 'upload' && (this.uploadHelp = false);
    this.onClean(el);
  }

  /**
   * Deletes variable mapping
   */
  public onDelete(el: HTMLElement, formEl: AbstractControl, scope: string) {
    const ctrlName = this.getFormControlNameForReturnValue(scope);
    const pathHash = this.container.pathHash;
    const variableName = formEl.value;

    this.questionnaireVariables.deleteVariableMapping(scope, variableName, pathHash);
    this.questionnaireState.onUpdateElement.emit({element: null, position: null});

    this.variablesForm.get(ctrlName) && this.variablesForm.get(ctrlName).setValue('');
    formEl.reset('');
    scope === 'upload' && (this.uploadHelp = false);
    this.onClean(el);
  }

  /**
   * Marks an element as dirty
   */
  public onDirty(el: HTMLElement, formEl: AbstractControl = null) {
    this.domHelper.addClasses(el, ['qa-tr-dirty']);
    formEl && formEl.markAsDirty();
    // badge and name hint follow the typing right away
    this.ref.detectChanges();
  }

  /**
   * Marks an element as clean
   */
  public onClean(el: HTMLElement) {
    this.domHelper.removeClasses(el, ['qa-tr-dirty']);
    this.domHelper.addClasses(el, ['qa-tr-clean', 'fadeIn', 'animated']);
    // badge and name hint disappear right away after apply, undo or delete
    this.ref.detectChanges();
    setTimeout(() => this.domHelper.removeClasses(el, ['qa-tr-clean', 'fadeIn', 'animated']), 600);
  }

  /**
   * Handles keyboard shortcuts on variable input fields and textareas.
   * In single-line inputs: Enter saves the variable.
   * In textareas: Ctrl+Enter saves the variable, plain Enter is blocked.
   * Ctrl+S saves the variable and stops event propagation in both contexts.
   *
   * @param {KeyboardEvent} event
   * @param {HTMLElement} tr
   * @param {string} scope
   * @param {boolean} isTextarea
   */
  public onKeyDown(event: KeyboardEvent, tr: HTMLElement, scope: string, isTextarea = false) {
    const isCtrlS = (event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'S');
    const isCtrlEnter = (event.ctrlKey || event.metaKey) && event.key === 'Enter';
    const isEnter = event.key === 'Enter';

    if (isTextarea && isEnter && !isCtrlEnter && !isCtrlS) {
      event.preventDefault();
      return;
    }

    const shouldSave = isCtrlS || (isTextarea ? isCtrlEnter : isEnter);

    if (!shouldSave) {
      return;
    }

    event.preventDefault();

    if (isCtrlS) {
      event.stopPropagation();
    }

    const formEl = this.variablesForm.get(scope);
    const isInvalid = !this.variablesForm.valid;
    const isNotDirty = !formEl.dirty;
    const isEmpty = !formEl.value;

    if (isInvalid || isNotDirty || isEmpty) {
      return;
    }

    this.onSave(tr, formEl, scope);
  }

  /**
   * Explains in plain words why the name of a changed variable is not accepted
   */
  public nameError(scope: string): string {
    return variableNameMessage(this.variablesForm && this.variablesForm.get(scope), key => this._(key),
      scope === 'gdt' ? GDT_VARIABLE_NAME_RULES : VARIABLE_NAME_RULES);
  }

  public isDisabledScope(scope: string) {
    if (!this.disabledScopes) {
      return false;
    }

    if (this.disabledScopes[scope]) {
      return true;
    }

    if (!!this.disabledScopes['pdf-form'] && scope.match(/^pdf-form/)) {
      return true;
    }

    return false;
  }

  /**
   * Returns keys of given object
   */
  public getObjectKeys(obj: any): string[] {
    return typeof obj !== 'object' ? [] : Object.keys(obj);
  }

  /**
   * Initializes variables form
   */
  private async initForm() {
    const pathHash = this.container.pathHash;
    const variables = this.questionnaire.meta.options.variables;
    const varNameValidator = Validators.pattern(VARIABLE_NAME_PATTERN);
    const gdtVarNameValidator = Validators.pattern(/^[a-zA-Z0-9_]{2,49}$/);
    const dynFormGroup = {};

    const pdfFormScopes = await this.questionnaireVariables.getPdfFormScopes();
    Object.assign(this.variableScopes, pdfFormScopes);
    this.scopeNames = [...this.scopeNames, ...Object.keys(pdfFormScopes)];

    Object.keys(pdfFormScopes).forEach((scopeName) => {
      !this.questionnaire.meta.options.variables[scopeName] && (this.questionnaire.meta.options.variables[scopeName] = {});
    });

    for (const scope of Object.keys(this.variableScopes)) {
      if (this.disabledScopes && this.isDisabledScope(scope)) {
        continue;
      }

      const validators = [];

      const variableScope = this.variableScopes[scope];

      if (!variableScope.skipValidation) {
        if (scope === 'gdt') {
          validators.push(gdtVarNameValidator);
        } else {
          // dropdown-only scopes: values come from mappings (e.g. custom.myKey contains a dot)
          const hasNonEmptyMappings = variableScope.mappings
            && Object.keys(variableScope.mappings).length > 0;
          if (!hasNonEmptyMappings) {
            validators.push(varNameValidator);
          }
        }
      }

      if (!variableScope.allowAccumulation) {
        validators.push(duplicateVariableNameValidator(scope, pathHash, variables));
      }

      dynFormGroup[scope] = this.formBuilder.control(null, validators);

      // add form element for return value, if allowed by scope
      if (variableScope.allowReturnValue) {
        const ctrlName = this.getFormControlNameForReturnValue(scope);
        dynFormGroup[ctrlName] = this.formBuilder.control(null);
      }
    }

    this.variablesForm = this.formBuilder.group(dynFormGroup);
  }

  /**
   * Populates variables form by current questionnaire
   */
  private populateForm() {
    const pathHash = this.container.pathHash;
    const variables: Questionnaire.Options.Variables = this.questionnaire.meta.options.variables;

    for (const scope of Object.keys(variables)) {
      const variableName = this.questionnaireVariables.getMappedVariableName(scope, pathHash);
      const options = this.questionnaireVariables.getMappedVariableOptions(scope, pathHash, variableName);

      if (!variableName) {
        continue;
      }

      // options has a return value? set related form field...
      if (options && options.returnValue && options.returnValue[this.locale]) {
        const returnValue = options.returnValue[this.locale];
        const ctrlName = this.getFormControlNameForReturnValue(scope);
        this.variablesForm.get(ctrlName) && this.variablesForm.get(ctrlName).setValue(returnValue);
      }

      this.variablesForm.get(scope) && this.variablesForm.get(scope).setValue(variableName);
    }

    this.ref.markForCheck();
    this.ref.detectChanges();
  }

  /**
   * Returns form control name for return value
   *
   * @param scope
   * @returns string
   */
  private getFormControlNameForReturnValue(scope: string) {
    return `${scope}-return-value`;
  }

  /**
   * Runs validation on given form element
   *
   * @param formEl
   */
  private runValidationOnFormEl(formEl: AbstractControl) {
    formEl.updateValueAndValidity();
    this.ref.markForCheck();
    this.ref.detectChanges();
  }
}
